import { supabase } from "./supabase";

const slugify = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const computeBrandKey = (explicitKey, fallbackName) => {
  const source = explicitKey && explicitKey.trim().length > 0 ? explicitKey : fallbackName;
  const normalized = slugify(source);
  return normalized;
};

export const brandService = {
  /**
   * Get all brands with their products
   */
  async getAllBrands() {
    try {
      const { data, error } = await supabase
        .from("brands")
        .select(`
          *,
          brand_products (
            product_id,
            products (
              id,
              name,
              description,
              short_description,
              main_image_url,
              additional_images,
              is_active,
              created_at,
              updated_at
            )
          )
        `)
        .order("display_order", { ascending: true })
        .order("name", { ascending: true });

      if (error) {
        console.error("❌ Error fetching brands:", error);
        throw error;
      }

      // Transform the data to include products array
      const transformedData = data.map((brand) => ({
        ...brand,
        brandKey: brand.brand_key,
        products: brand.brand_products?.map((bp) => bp.products).filter(Boolean) || []
      }));

      return { success: true, data: transformedData };
    } catch (error) {
      console.error("❌ Error in getAllBrands:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get all products (for brand creation/editing)
   */
  async getAllProducts() {
    try {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, description, is_active")
        .order("name", { ascending: true });

      if (error) {
        console.error("❌ Error fetching products:", error);
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error("❌ Error in getAllProducts:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Create a new brand
   */
  async createBrand(brandData) {
    try {
      console.log("🚀 Creating brand:", brandData);

      const brandKey = computeBrandKey(brandData.brandKey ?? brandData.brand_key, brandData.name);
      if (!brandKey) {
        throw new Error("Brand key could not be derived. Please provide a valid brand key.");
      }

      // Create the brand
      const { data: brand, error: brandError } = await supabase
        .from("brands")
        .insert({
          name: brandData.name,
          description: brandData.description,
          is_active: brandData.is_active ?? true,
          display_order: brandData.display_order ?? 0,
          brand_key: brandKey,
        })
        .select()
        .single();

      if (brandError) {
        console.error("❌ Error creating brand:", brandError);
        throw brandError;
      }

      // Add products to the brand if provided
      if (brandData.productIds && brandData.productIds.length > 0) {
        const brandProducts = brandData.productIds.map(productId => ({
          brand_id: brand.id,
          product_id: productId,
        }));

        const { error: productsError } = await supabase
          .from("brand_products")
          .insert(brandProducts);

        if (productsError) {
          console.error("❌ Error adding products to brand:", productsError);
          throw productsError;
        }
      }

      console.log("✅ Brand created successfully:", brand);
      return { success: true, data: brand };
    } catch (error) {
      console.error("❌ Error in createBrand:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Update a brand
   */
  async updateBrand(brandId, brandData) {
    try {
      console.log("🚀 Updating brand:", brandId, brandData);

      const brandKeyInput = brandData.brandKey ?? brandData.brand_key;
      const derivedBrandKey =
        brandKeyInput !== undefined
          ? computeBrandKey(brandKeyInput, brandData.name)
          : undefined;

      if (derivedBrandKey !== undefined && !derivedBrandKey) {
        throw new Error("Brand key cannot be empty.");
      }

      const payload = {
        name: brandData.name,
        description: brandData.description,
        is_active: brandData.is_active,
        display_order: brandData.display_order,
      };

      if (derivedBrandKey !== undefined) {
        payload.brand_key = derivedBrandKey;
      }

      // Update the brand
      const { data: brand, error: brandError } = await supabase
        .from("brands")
        .update(payload)
        .eq("id", brandId)
        .select()
        .single();

      if (brandError) {
        console.error("❌ Error updating brand:", brandError);
        throw brandError;
      }

      // Update brand products if provided
      if (brandData.productIds !== undefined) {
        // Remove existing products
        const { error: removeError } = await supabase
          .from("brand_products")
          .delete()
          .eq("brand_id", brandId);

        if (removeError) {
          console.error("❌ Error removing existing products:", removeError);
          throw removeError;
        }

        // Add new products
        if (brandData.productIds.length > 0) {
          const brandProducts = brandData.productIds.map(productId => ({
            brand_id: brandId,
            product_id: productId,
          }));

          const { error: addError } = await supabase
            .from("brand_products")
            .insert(brandProducts);

          if (addError) {
            console.error("❌ Error adding new products:", addError);
            throw addError;
          }
        }
      }

      console.log("✅ Brand updated successfully:", brand);
      return { success: true, data: brand };
    } catch (error) {
      console.error("❌ Error in updateBrand:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete a brand (doesn't delete products)
   */
  async deleteBrand(brandId) {
    try {
      console.log("🚀 Deleting brand:", brandId);

      // Delete the brand (cascade will handle brand_products)
      const { error } = await supabase
        .from("brands")
        .delete()
        .eq("id", brandId);

      if (error) {
        console.error("❌ Error deleting brand:", error);
        throw error;
      }

      console.log("✅ Brand deleted successfully");
      return { success: true };
    } catch (error) {
      console.error("❌ Error in deleteBrand:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Update brand display order
   */
  async updateBrandOrder(brandId, displayOrder) {
    try {
      const { data, error } = await supabase
        .from("brands")
        .update({ display_order: displayOrder })
        .eq("id", brandId)
        .select()
        .single();

      if (error) {
        console.error("❌ Error updating brand order:", error);
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error("❌ Error in updateBrandOrder:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Fetch commission tiers for a brand.
   * Defaults to the currently active tiers; includeFuture=true to fetch the full schedule.
   */
  async getCommissionTiers(brandKey, options = {}) {
    if (!brandKey) {
      return { success: false, error: "Brand key is required" };
    }

    try {
      const includeFuture = options.includeFuture ?? false;
      const source = includeFuture ? "commission_tier_config" : "active_commission_tiers";

      const query = supabase
        .from(source)
        .select("*")
        .eq("brand", brandKey)
        .order("effective_from_date", { ascending: true })
        .order("tier_min_roas", { ascending: true });

      const { data, error } = await query;

      if (error) {
        console.error("❌ Error fetching commission tiers:", error);
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error("❌ Error in getCommissionTiers:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Schedule a new commission tier set for a brand.
   */
  async scheduleCommissionTiers({ brandKey, tiers, effectiveFrom, actor, notes }) {
    if (!brandKey) {
      return { success: false, error: "Brand key is required" };
    }

    if (!Array.isArray(tiers) || tiers.length === 0) {
      return { success: false, error: "At least one tier is required" };
    }

    if (!effectiveFrom) {
      return { success: false, error: "Effective date is required" };
    }

    try {
      const payload = {
        p_brand: brandKey,
        p_effective_from: effectiveFrom,
        p_tiers: tiers,
        p_actor: actor ?? null,
        p_notes: notes ?? null,
      };

      const { data, error } = await supabase.rpc("set_commission_tiers", payload);

      if (error) {
        console.error("❌ Error scheduling commission tiers:", error);
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error("❌ Error in scheduleCommissionTiers:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete a scheduled commission tier set for a brand.
   */
  async deleteCommissionTierSchedule({ brandKey, effectiveFrom }) {
    if (!brandKey) {
      return { success: false, error: "Brand key is required" };
    }

    if (!effectiveFrom) {
      return { success: false, error: "Effective date is required" };
    }

    try {
      const payload = {
        p_brand: brandKey,
        p_effective_from: effectiveFrom,
      };

      const { data, error } = await supabase.rpc("delete_commission_tier_schedule", payload);

      if (error) {
        console.error("? Error deleting commission tier schedule:", error);
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error("? Error in deleteCommissionTierSchedule:", error);
      return { success: false, error: error.message };
    }
  },
};
